import { Dispatch, SetStateAction, useState } from 'react';
import { QueryObserverResult } from 'react-query';

import { PaymentLinksIcon, QuickTransferIcon } from '../../../../assets/svg';
import { ETPayButton } from '../../../../components/button';
import { DateAndTime } from '../../../../components/date-and-time';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { Status } from '../../../../components/status';
import { ETPayTable } from '../../../../components/table';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { RefundsData } from '../../../../models/refund';
import {
	ColumnInterface,
	Filters,
	FilterStatus,
	Pagination,
	RowInterface,
} from '../../../../models/table';
import { RefundsServices } from '../../../../services/refund';
import { validatePermission } from '../../../../utils/common';
import { errorFormatter } from '../../../../utils/errors';
import { fillStatesWithZeros } from '../../../../utils/states';
import { RefundsModal } from '../modal';
import {
	AmountContainer,
	Content,
	DetailsContainer,
	ExportModalContent,
	ExportModalDescription,
	ExportModalTitle,
	ItemTable,
	ProductContainer,
	Section,
	SubTitle,
	Title,
	TitleItem,
	Value,
} from './styles';

interface RefundsTableProps {
	data: RefundsData[];
	isLoading?: boolean;
	filters: Filters;
	onFilterChange: Dispatch<SetStateAction<Filters>>;
	pagination: Pagination;
	setPagination: Dispatch<SetStateAction<Pagination>>;
	totalResults?: number;
	setSelectedRowData: Dispatch<SetStateAction<RefundsData | undefined>>;
	selectedRowData: RefundsData | undefined;
	setShowAlert: (value: boolean) => void;
	showAlert: boolean;
	refetch: () => Promise<QueryObserverResult<any, unknown>>;
	stateList: FilterStatus[];
}

interface ModalProps {
	title: string;
	message: string;
	loader: boolean;
	show: boolean;
	url: string;
}

const defaultModal: ModalProps = {
	title: 'Exportar',
	message: 'Cargando la información',
	loader: false,
	show: false,
	url: '',
};

const RefundsTable = ({
	data,
	isLoading,
	filters,
	onFilterChange,
	pagination,
	setPagination,
	totalResults,
	setSelectedRowData,
	selectedRowData,
	setShowAlert,
	refetch,
	stateList,
}: RefundsTableProps) => {
	const { merchantSelected } = useMerchant();
	const [modal, setModal] = useState<ModalProps>(defaultModal);
	const [showModal, setShowModal] = useState(false);
	const { handleShowAlert } = useFloatingAlert();

	const handleClick = (rowData: RefundsData) => () => {
		setSelectedRowData(rowData);
		setShowModal(true);
	};

	const onClose = () => {
		setShowModal(false);
		refetch();
	};

	const getColumns = () => {
		const columns: ColumnInterface<RefundsData>[] = [
			{
				sortField: true,
				title: 'Referencia',
				propertyName: 'reference',
				cellRender: ({ reference }) => <>{reference}</>,
			},
			{
				title: 'Producto',
				propertyName: 'productType',
				cellRender: ({ productType }) => (
					<>
						<ProductContainer>
							{productType === 'QT' || productType === 'QM' ? (
								<QuickTransferIcon width="24" height="24" />
							) : (
								<PaymentLinksIcon width="24" height="24" />
							)}
						</ProductContainer>
					</>
				),
			},
			{
				sortField: true,
				title: 'Monto',
				align: 'right',
				propertyName: 'amount',
				cellRender: ({ amount }) => <AmountContainer>{amount}</AmountContainer>,
			},
			{
				sortField: true,
				title: 'Cuenta',
				propertyName: 'bankName',
				cellRender: ({ bankName, userClabe }) => (
					<ItemTable>
						<TitleItem>{userClabe}</TitleItem>
						<SubTitle>{bankName}</SubTitle>
					</ItemTable>
				),
			},
			{
				sortField: false,
				title: 'Medio de pago',
				propertyName: 'paymentMethod',
				cellRender: ({ paymentMethod }) => <>{paymentMethod}</>,
			},
			{
				title: 'Estatus',
				propertyName: 'status',
				cellRender: ({ status }) => status && <Status status={status} borderFull />,
			},
			{
				sortField: true,
				title: 'Fecha de creación',
				propertyName: 'createdatDate',
				cellRender: ({ createdatDate, createdatTime }) => (
					<DateAndTime date={createdatDate} time={createdatTime} />
				),
			},
			{
				title: '',
				propertyName: 'button',
				cellRender: (rowData: RefundsData) => (
					<>
						{(rowData.performReturn || rowData.status === 'error') &&
							merchantSelected?.role_id === 2 && (
								<ETPayButton title="Devolver" onClick={handleClick(rowData)} />
							)}
					</>
				),
			},
		];

		return columns;
	};

	const columns = getColumns();

	const detailSection: RowInterface<RefundsData> = (data) => {
		return (
			<DetailsContainer>
				<Section>
					<Content>
						<Title>Nombre del cliente</Title>
						<Value>{data.clientname}</Value>
					</Content>
					{merchantSelected?.country === 'MX' &&
						(data.paymentMethod === 'SPEI' || data.paymentMethod === 'Efectivo') && (
							<Content>
								<Title>Correo electrónico</Title>
								<Value>{data.uncensoredEmail}</Value>
							</Content>
						)}

					{merchantSelected?.country === 'MX' && data.paymentMethod === 'CoDi' && (
						<Content>
							<Title>Número de teléfono</Title>
							<Value>{data.uncensoredPhone}</Value>
						</Content>
					)}

					<Content>
						<Title>Fecha de expiración</Title>
						<Value>{data.expiredate}</Value>
					</Content>
					<Content>
						<Title>Fecha de validación</Title>
						<Value>
							{data.validatedatDate} {data.validatedatTime}
						</Value>
					</Content>
				</Section>
				<Section>
					<Content>
						<Title>Payment ID</Title>
						<Value>{data.paymentId}</Value>
					</Content>
				</Section>
			</DetailsContainer>
		);
	};

	const handleExportAction = async () => {
		setModal({ ...modal, loader: true, show: true });

		try {
			const response = await RefundsServices.getExportData({
				merchantCode: merchantSelected?.code as string,
				dateRange: {
					from: filters?.dateRange?.from,
					to: filters?.dateRange?.to,
				},
				search: filters?.search,
				stateSelected: filters?.stateSelected,
			});

			if (response.url) setModal({ ...modal, loader: false, show: true, url: response.url });
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: errorFormatter(e.code),
			});
		}
		return;
	};

	const handleCloseModal = () => {
		setModal(defaultModal);
	};

	const handleDownloadData = () => {
		if (modal.url) window.location.assign(modal.url);
		setModal(defaultModal);
	};

	const refundStates = ['success'];

	return (
		<>
			{modal.show && (
				<Modal
					title={modal.title}
					show={modal.show}
					onClose={handleCloseModal}
					allowCloseModal={!modal.loader}
				>
					{modal.loader ? (
						<Spinner message={modal.message} />
					) : (
						<ExportModalContent>
							<ExportModalTitle>Tu reporte está listo</ExportModalTitle>

							<ExportModalDescription>
								Hemos terminado de recopilar los datos de tu reporte. Haz clic en el botón para
								descargarlo.
							</ExportModalDescription>
							<ETPayButton
								title="Descargar reporte"
								variant="effect"
								onClick={handleDownloadData}
							/>
						</ExportModalContent>
					)}
				</Modal>
			)}

			{showModal && (
				<RefundsModal
					show={showModal}
					onClose={onClose}
					selectedRowData={selectedRowData}
					setShowAlert={setShowAlert}
				/>
			)}

			<ETPayTable<RefundsData>
				title={'Todos los cobros'}
				data={data}
				isLoading={!!isLoading}
				columns={columns}
				row={detailSection}
				hasFilters
				hasDateFilter
				allowExport={validatePermission('REFUNDS_EXPORT')}
				exportAction={handleExportAction}
				filters={filters}
				onFilterChange={onFilterChange}
				hasSearch
				hasPagination
				totalResults={totalResults}
				pagination={pagination}
				setPagination={setPagination}
				emptyMessage="No hay información disponible en el periodo seleccionado"
				stateList={fillStatesWithZeros({
					states: refundStates,
					stateList,
				})}
				statusContent={[['success', 'Exitosos']]}
			/>
		</>
	);
};

export { RefundsTable };
